'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import useCinemaRoomEquipment from '@cineamo/composables/src/hooks/useCinemaRoomEquipment';

import { IconMap16 } from '@cineamo/ui/src/icons/ic-map';
import { IconPicture16 } from '@cineamo/ui/src/icons/ic-picture';
import CinemaDescriptionMarkdown, {
    type CinemaDescriptionMarkdownProps
} from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';
import Modal, { ModalProps } from '@cineamo/ui/src/modals/Modal';

import { useSeatMap } from '../hooks/useSeatMap';

import CinemaRoomImages from './CinemaRoomImages';
import CinemaRoomSeatMap from './CinemaRoomSeatMap';

type CinemaRoomsModalProps = ModalProps & {
    cinemaRoom: CinemaRoomDto;
};

const CinemaRoomDetailModal: React.FC<CinemaRoomsModalProps> = (props) => {
    const { cinemaRoom } = props;

    const { equipmentGroups, seatsCountByType } = useCinemaRoomEquipment(cinemaRoom);
    const { seats } = useSeatMap(cinemaRoom?.id);
    const t = useTranslations();

    const [selectedTab, setSelectedTab] = useState<'seat_map' | 'images'>('images');

    const descriptionGroupTitle = t('cinema.equipment.description');

    // Matches the text-body-12 sizing of the equipment labels below, since CinemaDescriptionMarkdown's
    // default paragraph styling (text-body-14) is meant for larger description blocks elsewhere.
    const descriptionComponents: CinemaDescriptionMarkdownProps['components'] = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        p: ({ node, ...paragraphProps }) => (
            <p
                {...paragraphProps}
                className="text-body-12 leading-[22px]"
            />
        )
    };

    const equipmentList = equipmentGroups.map((equipmentGroup) => (
        <div key={`equipmentGroup#${equipmentGroup.title}`}>
            <h3 className="text-body-bold-14">{equipmentGroup.title}</h3>
            {equipmentGroup.title === descriptionGroupTitle ? (
                // The description holds free-form markdown text (unlike the short equipment labels below),
                // so it gets its own full-width block instead of the icon-row flex layout.
                <CinemaDescriptionMarkdown
                    description={equipmentGroup.items[0]?.name}
                    components={descriptionComponents}
                />
            ) : (
                <div className="flex flex-row flex-wrap gap-x-16 gap-y-2 text-body-12 leading-[22px]">
                    {equipmentGroup.items.map((equipmentListItem) => (
                        <div
                            key={`equipmentListItem#${equipmentListItem.name}`}
                            className="flex flex-row space-x-6 items-center">
                            {equipmentListItem.icon?.({ className: 'w-16 h-16 flex-none align-text-bottom' })}
                            <span>{equipmentListItem.name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ));

    const hasVisibleTabs = seats.length > 0;

    return (
        <Modal
            {...props}
            title={cinemaRoom.name}
            className="max-w-[888px] max-h-[640px]"
            titleClassName="text-headline-24"
            onClose={null}>
            <div className="h-full flex flex-col lg:flex-row lg:space-x-24 overflow-auto">
                <div className="w-full lg:w-1/2 flex flex-col space-y-16 mb-24 lg:mb-0">{equipmentList}</div>

                <div className="w-full lg:w-0 lg:flex-auto flex flex-col space-y-6">
                    {hasVisibleTabs && (
                        <div className="flex flex-row space-x-16">
                            {seats.length > 0 && (
                                // Show "pictures" tab only if it's not the only visible tab.
                                <button
                                    className={cn(
                                        'py-4 px-8 flex flex-row space-x-8 items-center rounded-4 text-body-bold-12',
                                        selectedTab !== 'images'
                                            ? 'text-[rgba(var(--on-surface-color-rgb),0.5)]'
                                            : 'bg-[rgba(var(--on-surface-color-rgb),0.15)] text-[var(--on-surface-color)]'
                                    )}
                                    onClick={() => setSelectedTab('images')}>
                                    <IconPicture16 />
                                    <span>{t('cinema.pictures')}</span>
                                </button>
                            )}
                            {seats.length > 0 && (
                                <button
                                    className={cn(
                                        'py-4 px-8 flex flex-row space-x-8 items-center rounded-4 text-body-bold-12',
                                        selectedTab !== 'seat_map'
                                            ? 'text-[rgba(var(--on-surface-color-rgb),0.5)]'
                                            : 'bg-[rgba(var(--on-surface-color-rgb),0.15)] text-[var(--on-surface-color)]'
                                    )}
                                    onClick={() => setSelectedTab('seat_map')}>
                                    <IconMap16 />
                                    <span>{t('cinema.seat-map')}</span>
                                </button>
                            )}
                        </div>
                    )}
                    {selectedTab === 'images' && <CinemaRoomImages cinemaRoom={cinemaRoom} />}
                    {selectedTab === 'seat_map' && (
                        <CinemaRoomSeatMap
                            cinemaRoom={cinemaRoom}
                            seatsCountByType={seatsCountByType}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CinemaRoomDetailModal;
